import { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  TextField,
  Grid,
  Typography,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import ConfirmFreeTrialModal from './ConfirmFreeTrialModal';
import COLORS from '@/resources/style/Themes';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { ToastType } from '@/context_providers/toast_context/ToastContext';
import {
  addOrganization,
  updateOrganizationViaAdmin,
  addOrganizationNote,
} from '@/repository/OrganizationDAO';
import { inputSanitization } from '@/utils/app_support/InputSanitizaiton';
import {
  OrganizationOnAzure,
  OrganizationSalesCreate,
  SubscriptionTier,
} from '@/../generated-client/models';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import { EMAIL_REGEX } from '@/utils/validation/emailValidation';

type ValidationSchema = {
  organizationName: string;
  orgCode: string;
  address: string;
  email: string;
  phoneNumber: string;
  primaryContact: string;
};

const CreateOrganizationModal = ({
  show,
  onHide,
  onCreate,
}: {
  show: boolean;
  onHide: () => void;
  onCreate: (newOrg: OrganizationOnAzure) => void;
}) => {
  const [organizationName, setOrganizationName] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValidPhone, setIsValidPhone] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [primaryContact, setPrimaryContact] = useState('');
  const [note, setNote] = useState('');
  const [isSafeguardOrganization, setIsSafeguardOrganization] = useState(false);
  const [opsSubscriptionCount, setOpsSubscriptionCount] = useState(0);
  const [respondSubscriptionCount, setRespondSubscriptionCount] = useState(0);
  const [trialEnabled, setTrialEnabled] = useState(false);
  const [trialEndTimestamp, setTrialEndTimestamp] = useState<string>('');
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const [validation, setValidation] = useState<ValidationSchema>({
    organizationName: '',
    orgCode: '',
    address: '',
    email: '',
    phoneNumber: '',
    primaryContact: '',
  });

  useEffect(() => {
    const today = new Date();
    const ninetyDaysLater = new Date(today);
    ninetyDaysLater.setDate(today.getDate() + 90);
    const ninetyDaysLaterStr = ninetyDaysLater.toISOString().substring(0, 10);
    setTrialEndTimestamp(ninetyDaysLaterStr);
  }, [show]);

  const validateFields = () => {
    let isValid = true;
    const normalizedEmail = email.trim();
    const newValidation: ValidationSchema = {
      organizationName: '',
      orgCode: '',
      address: '',
      email: '',
      phoneNumber: '',
      primaryContact: '',
    };

    if (organizationName.length === 0) {
      newValidation.organizationName = 'error';
      isValid = false;
    }

    if (orgCode.length === 0 || orgCode.length < 8) {
      newValidation.orgCode = 'error';
      isValid = false;
    }

    if (address.length === 0) {
      newValidation.address = 'error';
      isValid = false;
    }

    if (normalizedEmail && !EMAIL_REGEX.test(normalizedEmail)) {
      newValidation.email = 'error';
      isValid = false;
    }

    if (!matchIsValidTel(phoneNumber)) {
      newValidation.phoneNumber = 'error';
      isValid = false;
    }

    if (primaryContact.length === 0) {
      newValidation.primaryContact = 'error';
      isValid = false;
    }

    setValidation(newValidation);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }
    const normalizedEmail = email.trim();
    const emailToSave = normalizedEmail === '' ? null : normalizedEmail;

    const trialEndDateObj = trialEnabled
      ? new Date(trialEndTimestamp)
      : new Date();
    trialEndDateObj.setHours(0, 0, 0, 0);
    const trialLength = trialEndDateObj.getTime();

    const newOrg: OrganizationSalesCreate = {
      features: null,
      roles_with_features_custom: null,
      company_name: organizationName,
      org_code: orgCode,
      address: address,
      email: emailToSave,
      phone_number: phoneNumber,
      primary_contact: primaryContact,
      is_safeguard_organization: isSafeguardOrganization,
      subscription_start_timestamp: 1704110400000, //January 1st, 2024
      subscription_duration: 27759, //January 1st, 2100
      trial_end_timestamp: trialLength,
      subscription_count: 0, // legacy field, superseded by ops/respond pools; kept 0 on create
      ops_subscription_count: opsSubscriptionCount,
      respond_subscription_count: respondSubscriptionCount,
    };

    try {
      setIsLoading(true);
      const createdOrg = await addOrganization(newOrg);

      await updateOrganizationViaAdmin({
        address: createdOrg.address,
        company_name: createdOrg.company_name,
        email: createdOrg.email,
        join_org_pin: createdOrg.join_org_pin,
        org_code: createdOrg.org_code,
        organization_azure_id: createdOrg.id,
        phone_number: createdOrg.phone_number,
        primary_contact: createdOrg.primary_contact,
        roles_with_features_custom: createdOrg.roles_with_features_custom,
      });

      showToast(
        `Successfully added ${createdOrg.company_name}.`,
        ToastType.SUCCESS,
      );
      onCreate(createdOrg);

      if (note !== '') {
        const newNote = {
          note_name: new Date().toISOString(),
          note: note,
          org_code: createdOrg.org_code,
          organization_azure_id: createdOrg.id,
        };

        try {
          await addOrganizationNote(newNote);
        } catch {
          showToast(
            `Failed to add organization note. Please try again.`,
            ToastType.DANGER,
          );
        }
      }

      resetValues();
      onHide();
    } catch {
      showToast(
        `Failed to add ${newOrg.company_name}. Please try again.`,
        ToastType.DANGER,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetValues = () => {
    setOrganizationName('');
    setOrgCode('');
    setAddress('');
    setEmail('');
    setPhoneNumber('');
    setPrimaryContact('');
    setIsSafeguardOrganization(false);
    setOpsSubscriptionCount(0);
    setRespondSubscriptionCount(0);
    setTrialEnabled(false);
    setTrialEndTimestamp('');
    setPhoneTouched(false);
    setValidation({
      organizationName: '',
      orgCode: '',
      address: '',
      email: '',
      phoneNumber: '',
      primaryContact: '',
    });
  };

  const handleTrialChange = (e: SelectChangeEvent) => {
    setTrialEnabled(e.target.value === 'Enabled');
    if (e.target.value === 'Enabled') {
      setConfirmationDialogOpen(true);
    }
  };

  const handleConfirmationClose = () => {
    setTrialEnabled(false);
    setConfirmationDialogOpen(false);
  };

  const handleConfirmationAgree = () => {
    setTrialEnabled(true);
    setConfirmationDialogOpen(false);
  };

  return (
    <>
      <Modal
        open={show}
        onClose={() => {
          onHide();
          resetValues();
        }}
        aria-labelledby="create-organization-modal-title"
        aria-describedby="create-organization-modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: { xs: '90%', sm: '80%', md: '60%', lg: '50%' },
            bgcolor: 'background.paper',
            transform: { xs: 'scale(0.9)', xl: 'none' },
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            outline: 'none',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              backgroundColor: COLORS.SAFEGUARD_BLUE,
              color: COLORS.WHITE,
              padding: 2,
              borderTopLeftRadius: 2,
              borderTopRightRadius: 2,
            }}
          >
            <Typography id="create-organization-modal-title" variant="h6">
              Add New Organization
            </Typography>
          </Box>

          {/* Modal Content */}
          <Box component="form" sx={{ p: 4 }}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  fullWidth
                  label="Organization Name"
                  variant="outlined"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  error={validation.organizationName === 'error'}
                  helperText={
                    validation.organizationName === 'error'
                      ? 'Organization name cannot be empty.'
                      : ''
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  fullWidth
                  label="Organization Code"
                  variant="outlined"
                  value={orgCode}
                  onChange={(e) => setOrgCode(inputSanitization(e))}
                  inputProps={{ maxLength: 8 }}
                  error={validation.orgCode === 'error'}
                  helperText={
                    validation.orgCode === 'error'
                      ? 'Organization Code must be 8 characters.'
                      : ''
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  fullWidth
                  label="Address"
                  variant="outlined"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  error={validation.address === 'error'}
                  helperText={
                    validation.address === 'error'
                      ? 'Address cannot be empty.'
                      : ''
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  fullWidth
                  label="Primary Contact"
                  variant="outlined"
                  value={primaryContact}
                  onChange={(e) => setPrimaryContact(e.target.value)}
                  error={validation.primaryContact === 'error'}
                  helperText={
                    validation.primaryContact === 'error'
                      ? 'Primary contact cannot be empty.'
                      : ''
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <MuiTelInput
                  fullWidth
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(value) => {
                    setPhoneNumber(value);
                    setIsValidPhone(matchIsValidTel(value));
                    setPhoneTouched(true);
                  }}
                  onBlur={() => setPhoneTouched(true)}
                  error={phoneTouched && !isValidPhone}
                  helperText={
                    phoneTouched && !isValidPhone
                      ? 'Please enter a valid phone number.'
                      : ''
                  }
                  defaultCountry="US"
                  forceCallingCode
                  preferredCountries={['US']}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  fullWidth
                  label="Email (Optional)"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={validation.email === 'error'}
                  helperText={
                    validation.email === 'error'
                      ? 'Please provide a valid email address.'
                      : ''
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>Internal Safeguard Organization</InputLabel>
                  <Select
                    value={isSafeguardOrganization ? 'Yes' : 'No'}
                    onChange={(e) =>
                      setIsSafeguardOrganization(e.target.value === 'Yes')
                    }
                    label="Internal Safeguard Organization"
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>Enable Trial</InputLabel>
                  <Select
                    value={trialEnabled ? 'Enabled' : 'Disabled'}
                    onChange={handleTrialChange}
                    label="Enable Trial"
                  >
                    <MenuItem value="Enabled">Enabled</MenuItem>
                    <MenuItem value="Disabled">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {trialEnabled && (
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Trial End Date"
                    type="date"
                    variant="outlined"
                    value={trialEndTimestamp}
                    onChange={(e) => setTrialEndTimestamp(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              )}
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  fullWidth
                  label={`${getTierLabelKey(SubscriptionTier.Ops)} Subscription Count`}
                  variant="outlined"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={opsSubscriptionCount}
                  onChange={(e) =>
                    setOpsSubscriptionCount(Number(e.target.value))
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  fullWidth
                  label={`${getTierLabelKey(SubscriptionTier.Respond)} Subscription Count`}
                  variant="outlined"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={respondSubscriptionCount}
                  onChange={(e) =>
                    setRespondSubscriptionCount(Number(e.target.value))
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Note (Optional)"
                  variant="outlined"
                  multiline
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Footer Section */}
          <Box sx={{ textAlign: 'right', p: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Add'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmFreeTrialModal
        open={isConfirmationDialogOpen}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationAgree}
      />
    </>
  );
};

export default CreateOrganizationModal;
